import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSupplierInbox } from 'modules/FreeSupplier/supplierInboxApi';
import { INVITE_KINDS, operatorInviteQueryOptions } from './useFetchOperatorInvites';

// Same cache key as useSupplierInbox (SUPPLIER_INBOX_QUERY_KEY) so the badge
// and the supplier surfaces (OperatorDeclarantInbox, the Lite-shell inbox)
// share ONE cache entry instead of double-polling. Deliberately not imported
// from useSupplierInbox.js: that module pulls in the full mutation surface,
// and tests mock supplierInboxApi with only fetchSupplierInbox defined.
const SUPPLIER_INBOX_QUERY_KEY = ['supplierInbox'];

// Shared empty array so `data` keeps a stable identity while the queries are
// loading/disabled (consumers pass it straight into child props).
const EMPTY_LIST = [];

/**
 * Pending-request counts + payloads for the sidebar badge and the Overview
 * panel.
 *
 * Declarant-side requests come from the S3 supplier inbox — the same
 * `['supplierInbox']` cache entry every supplier surface reads — and count
 * only items still awaiting the operator. Each inbox item carries its own
 * `nextAction` (null once the connection — and every one of its requests —
 * is resolved: accepted with nothing left to review, declined, expired,
 * cancelled, or terminated); counting raw item length would badge history
 * the operator has already acted on.
 *
 * Operator-side requests share the Requests page's cache entry (see
 * useFetchOperatorInvites), so a user sitting on the Requests page doesn't
 * poll the same endpoint twice.
 *
 * Keeps the previous return shape: `{ data, isLoading, isError, refetch }`
 * where `data` carries counts and the raw arrays.
 *
 * @param {string} userType Only operators and consultants have incoming requests.
 * @param {{enabled?: boolean}} [options] Gate the fetches (e.g. until permissions resolve).
 */
export const useFetchOperatorInviteCount = (userType, { enabled = true } = {}) => {
  const isEligible = enabled && (userType === 'operator' || userType === 'consultant');

  const declarantQuery = useQuery({
    queryKey: SUPPLIER_INBOX_QUERY_KEY,
    queryFn: fetchSupplierInbox,
    enabled: isEligible,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute for real-time updates
  });
  const operatorQuery = useQuery(
    operatorInviteQueryOptions(INVITE_KINDS.OPERATOR, { enabled: isEligible }),
  );

  const declarantItems = Array.isArray(declarantQuery.data?.items)
    ? declarantQuery.data.items
    : EMPTY_LIST;
  const operatorData = Array.isArray(operatorQuery.data) ? operatorQuery.data : EMPTY_LIST;

  // A failed fetch reads as "nothing pending" rather than an error banner —
  // the badge is ambient chrome.
  const data = useMemo(() => {
    const actionableDeclarantItems = declarantItems.filter((item) => item.nextAction !== null);
    return {
      declarantCount: actionableDeclarantItems.length,
      operatorCount: operatorData.length,
      totalCount: actionableDeclarantItems.length + operatorData.length,
      declarantData: declarantItems,
      operatorData,
    };
  }, [declarantItems, operatorData]);

  return {
    data,
    isLoading: declarantQuery.isLoading || operatorQuery.isLoading,
    isSuccess: declarantQuery.isSuccess && operatorQuery.isSuccess,
    isError: declarantQuery.isError || operatorQuery.isError,
    refetch: () => {
      declarantQuery.refetch();
      operatorQuery.refetch();
    },
  };
};
